/**
 * The floating "+N" numbers, as elements above the yard rather than pixels in
 * it.
 *
 * The canvas draws at a native resolution and is scaled up by a whole number
 * with smoothing off (ui/pixelCanvas.ts), so a typeface drawn into it comes
 * back as steps. These sit over it instead and render at the screen's own
 * resolution, which is the only way a real face stays one here.
 *
 * What that costs: a float is no longer part of the picture. Nothing in the
 * scene can pass in front of one, and it will not appear in anything captured
 * from the canvas. Worth it for the one piece of type the yard shows.
 */

import type { PixelCanvas } from './pixelCanvas';

/** Type size in native pixels, so it follows the canvas's scale. */
const FONT_NATIVE = 11;
/**
 * How far one climbs, as a share of the yard's height rather than a fixed
 * distance: the canvas is as tall as the panel allows, so a number should
 * cross the same amount of sky whatever that works out to.
 */
const RISE_FRACTION = 0.5;
/** Side of an icon beside the text, in native pixels. */
const ICON_NATIVE = 16;
/** Cap, so an autoclicker cannot grow the layer without bound. */
const MAX_FLOATS = 40;
/** A jackpot's amount, in native pixels: a little over twice a click's. */
const JACKPOT_FONT_NATIVE = 24;
/** Native pixels a jackpot's figure keeps from the yard's edges. */
const JACKPOT_MARGIN = 4;
/** How far above the catch the figure is centred, in native pixels. */
const JACKPOT_LIFT = 10;

export interface FloatLayer {
  /**
   * `x` and `y` are native canvas pixels. The text is centred on `x` and sits
   * with its baseline area ending at `y`, so `y` is the point it rises from.
   * `icon`, a URL to pixel art 16 native pixels square, follows the text:
   * the shard after a meteor's "+3", so it says what was earned.
   */
  show(x: number, y: number, text: string, icon?: string): void;
  /**
   * An egg jackpot's amount, big, with a caption under it, popped out over a
   * catch at native (`x`, `y`) and kept inside the yard. Returns the native
   * point it is centred on, which is where the sunburst behind it goes.
   */
  showJackpot(x: number, y: number, amount: string, caption: string): { x: number; y: number };
}

export function createFloatLayer(pc: PixelCanvas): FloatLayer {
  const layer = document.createElement('div');
  layer.className = 'floats';
  // Decorative: the number is already announced by the egg count.
  layer.setAttribute('aria-hidden', 'true');
  pc.canvas.parentElement?.append(layer);

  return {
    show(x, y, text, icon) {
      while (layer.childElementCount >= MAX_FLOATS) layer.firstElementChild?.remove();
      const span = document.createElement('span');
      span.className = 'float';
      span.textContent = text;
      if (icon) {
        const img = document.createElement('img');
        img.className = 'float__icon';
        img.src = icon;
        img.alt = '';
        // Whole native pixels, like the canvas under it, so the art stays sharp.
        img.width = img.height = ICON_NATIVE * pc.scale;
        span.append(img);
      }
      // The canvas is centred in its panel and its scale changes with the
      // viewport, so both are read at spawn rather than assumed.
      const scale = pc.scale;
      span.style.left = `${pc.canvas.offsetLeft + x * scale}px`;
      span.style.top = `${pc.canvas.offsetTop + y * scale}px`;
      span.style.fontSize = `${FONT_NATIVE * scale}px`;
      span.style.setProperty('--rise', `${pc.height * RISE_FRACTION * scale}px`);
      // The animation owns the lifetime, so nothing has to tick these.
      span.addEventListener('animationend', () => span.remove());
      layer.append(span);
    },

    showJackpot(x, y, amount, caption) {
      while (layer.childElementCount >= MAX_FLOATS) layer.firstElementChild?.remove();
      const box = document.createElement('div');
      box.className = 'jackpot';
      const figure = document.createElement('span');
      figure.className = 'jackpot__amount';
      figure.textContent = amount;
      const note = document.createElement('span');
      note.className = 'jackpot__caption';
      note.textContent = caption;
      box.append(figure, note);
      const scale = pc.scale;
      box.style.fontSize = `${JACKPOT_FONT_NATIVE * scale}px`;
      box.addEventListener('animationend', () => box.remove());
      layer.append(box);

      // Measured once it is in, then pulled in from the edges: a catch near
      // the side of the yard would otherwise put half the figure off it. A
      // figure wider than the yard (a narrow phone) is simply centred.
      const halfW = box.offsetWidth / scale / 2;
      const halfH = box.offsetHeight / scale / 2;
      const within = (v: number, lo: number, hi: number): number =>
        lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v));
      const cx = within(x, halfW + JACKPOT_MARGIN, pc.width - halfW - JACKPOT_MARGIN);
      const cy = within(
        y - JACKPOT_LIFT,
        halfH + JACKPOT_MARGIN,
        pc.height - halfH - JACKPOT_MARGIN,
      );
      box.style.left = `${pc.canvas.offsetLeft + cx * scale}px`;
      box.style.top = `${pc.canvas.offsetTop + cy * scale}px`;
      return { x: cx, y: cy };
    },
  };
}
